import { useState, useEffect, useMemo } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import { sb } from '../lib/supabase';
import { fmt } from '../lib/helpers';
import Badge from '../components/Badge';
import { useRexScreen } from '../context/RexContext';

/**
 * Layer 2 — Project overview. Hero (stage pills + lifecycle progress) and
 * project-scoped metric tiles, with quick links into the pillars.
 */
export default function ProjectHome() {
  const { project, programme, stages, currentStageOrder } = useOutletContext();
  const navigate = useNavigate();
  const base = `/project/${project.id}`;
  const [stats, setStats] = useState({ raid: null, gates: null, milestones: null, workstreams: null });

  useEffect(() => {
    if (!project?.id) return;
    let cancelled = false;
    (async () => {
      const [raid, gates, milestones, ws] = await Promise.all([
        sb.from('raid_items').select('id', { count: 'exact', head: true }).eq('project_id', project.id).neq('status', 'closed'),
        sb.from('stage_gates').select('id', { count: 'exact', head: true }).eq('project_id', project.id),
        sb.from('plan_milestones').select('id', { count: 'exact', head: true }).eq('project_id', project.id),
        sb.from('workstreams').select('id', { count: 'exact', head: true }).eq('project_id', project.id),
      ]);
      if (cancelled) return;
      setStats({ raid: raid.count ?? 0, gates: gates.count ?? 0, milestones: milestones.count ?? 0, workstreams: ws.count ?? 0 });
    })();
    return () => { cancelled = true; };
  }, [project?.id]);

  const totalStages = stages.length || 1;
  const completedStages = currentStageOrder >= 0 ? currentStageOrder : 0;
  const progressPct = Math.round((completedStages / totalStages) * 100);
  const statusName = project.programme_statuses?.name || 'Draft';

  const tiles = [
    { label: 'Open RAID', value: stats.raid ?? '—', sub: 'risks, issues & more', seg: 'governance/raid' },
    { label: 'Stage gates', value: stats.gates ?? '—', sub: 'in pipeline', seg: 'governance/stage-gates' },
    { label: 'Milestones', value: stats.milestones ?? '—', sub: 'on the plan', seg: 'management/plan' },
    { label: 'Workstreams', value: stats.workstreams ?? '—', sub: 'in this project', seg: null },
  ];

  useRexScreen({
    snapshot: useMemo(() => ({
      projectName: project?.name,
      stage: project?.programme_stages?.name,
      status: statusName,
      progressPct,
      openRaid: stats.raid,
      gates: stats.gates,
      milestones: stats.milestones,
    }), [project?.name, project?.programme_stages?.name, statusName, progressPct, stats]),
  });

  return (
    <div className="content">
      <div className="prog-hero">
        <div className="prog-hero-top">
          <div>
            <div className="prog-kicker">Project</div>
            <div className="prog-title">{project.name}</div>
            <div className="prog-subtitle">
              {programme?.name}
              {project.target_go_live ? ` · Go-live: ${fmt(project.target_go_live)}` : ''}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
            <Badge variant="gr">{statusName}</Badge>
          </div>
        </div>

        {stages.length > 0 && (
          <div className="stage-pills">
            {stages.map((s) => {
              const cls = s.order_index < currentStageOrder ? 'done'
                : s.order_index === currentStageOrder ? 'current' : '';
              return <span key={s.id} className={`stage-pill ${cls}`}>{s.name}</span>;
            })}
          </div>
        )}

        <div className="hero-prog-wrap">
          <div className="hero-prog-bar"><div className="hero-prog-fill" style={{ width: `${progressPct}%` }} /></div>
          <span className="hero-prog-pct">{progressPct}% complete</span>
        </div>
      </div>

      <div className="m4">
        {tiles.map((t) => (
          <div
            key={t.label}
            className="met"
            onClick={t.seg ? () => navigate(`${base}/${t.seg}`) : undefined}
            style={t.seg ? { cursor: 'pointer' } : undefined}
          >
            <div className="mlbl">{t.label}</div>
            <div className="mval">{t.value}</div>
            <div className="msub">{t.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
